<template>
  <div class="container-fluid pb-4">
    <div v-if="message" class="alert alert-success d-flex justify-content-between align-items-center" role="alert">
      <span>{{ message }}</span>
      <button type="button" class="btn-close" aria-label="Cerrar" @click="message = ''"></button>
    </div>
    <div v-if="error" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span>{{ error }}</span>
      <button type="button" class="btn-close" aria-label="Cerrar" @click="error = ''"></button>
    </div>

    <div v-if="gmailStatus && (gmailStatus.connected || gmailStatus.can_manage)" class="gmail-status mb-3">
      <div>
        <i class="fab fa-google" aria-hidden="true"></i>
        <div>
          <strong>{{ gmailStatus.connected ? 'Gmail conectado' : 'Gmail pendiente de conexión' }}</strong>
          <span v-if="gmailStatus.connected">
            {{ gmailStatus.email || 'Cuenta autorizada' }} ·
            {{ gmailStatus.send_enabled ? 'Envío por API activo' : 'Envío por API desactivado en local' }}
            <template v-if="gmailStatus.send_enabled && (gmailStatus.allowed_recipients || []).length">
              · Limitado a {{ gmailStatus.allowed_recipients.join(', ') }}
            </template>
          </span>
          <span v-else>Autoriza la cuenta hola@marketinghl.com para habilitar el envío y la consulta de respuestas.</span>
        </div>
      </div>
      <div v-if="gmailStatus.can_manage" class="gmail-actions">
        <a href="/google/gmail/connect" class="btn btn-outline-primary btn-sm">
          <i class="fas fa-link" aria-hidden="true"></i>
          <span class="ms-2">{{ gmailStatus.connected ? 'Reconectar' : 'Conectar Gmail' }}</span>
        </a>
        <button v-if="gmailStatus.connected" type="button" class="btn btn-outline-danger btn-sm" @click="disconnectGmail">
          <i class="fas fa-unlink" aria-hidden="true"></i><span class="ms-2">Desconectar</span>
        </button>
      </div>
    </div>

    <div class="summary-bar mb-3">
      <button type="button" class="summary-item" :class="{ active: selectedStatus === '' }" @click="setStatusFilter('')">
        <span>Todos</span><strong>{{ totalCount }}</strong>
      </button>
      <button v-for="option in statusOptions" :key="option.value" type="button" class="summary-item"
        :class="{ active: selectedStatus === option.value }" @click="setStatusFilter(option.value)">
        <span>{{ option.label }}</span><strong>{{ counts[option.value] || 0 }}</strong>
      </button>
    </div>

    <div class="toolbar-row mb-3">
      <div class="search-box">
        <i class="fas fa-search" aria-hidden="true"></i>
        <input v-model.trim="search" type="search" class="form-control" placeholder="Buscar pedido, título, medio o contacto">
      </div>
      <div class="toolbar-actions">
        <button v-if="gmailStatus && gmailStatus.connected" type="button" class="btn btn-outline-primary"
          :disabled="syncLoading" @click="syncReplies">
          <span v-if="syncLoading" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          <i v-else class="fas fa-inbox" aria-hidden="true"></i>
          <span class="ms-2">Consultar respuestas</span>
        </button>
        <button type="button" class="btn btn-outline-secondary" title="Actualizar" @click="loadFollowUps(pagination.current_page)">
          <i class="fas fa-sync-alt" aria-hidden="true"></i>
          <span class="ms-2">Actualizar</span>
        </button>
      </div>
    </div>

    <div class="table-responsive follow-up-table-wrap">
      <table class="table table-hover align-middle mb-0">
        <thead>
          <tr>
            <th>Pedido</th>
            <th>Medio</th>
            <th>Contacto</th>
            <th>Estado</th>
            <th>Archivos</th>
            <th>Enviado</th>
            <th>Último seguimiento</th>
            <th>URL</th>
            <th class="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="9" class="empty-row">
              <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Cargando seguimientos...
            </td>
          </tr>
          <tr v-else-if="filteredFollowUps.length === 0">
            <td colspan="9" class="empty-row">No hay seguimientos con los filtros seleccionados.</td>
          </tr>
          <template v-else>
          <tr v-for="item in filteredFollowUps" :key="item.id">
            <td class="order-cell">
              <strong>P{{ item.pedido_id }}</strong>
              <span>{{ item.titulo }}</span>
            </td>
            <td>
              <a :href="item.website.sitio" target="_blank" rel="noopener">{{ domain(item.website.sitio) }}</a>
              <small>{{ item.cliente || 'Sin cliente' }}</small>
            </td>
            <td>
              <span>{{ item.website.nombre_saludo || item.website.contacto || 'Sin nombre' }}</span>
              <small>{{ item.website.email || 'Sin correo' }}</small>
            </td>
            <td><span class="status-badge" :class="statusClass(item.status)">{{ item.status_label }}</span></td>
            <td>
              <span>{{ attachmentSummary(item.current_attachments) }}</span>
            </td>
            <td>
              <span>{{ formatDate(item.sent_at) }}</span>
              <small v-if="item.days_since_sent !== null">{{ item.days_since_sent }} días</small>
            </td>
            <td>{{ formatDate(item.last_reminder_at) }}</td>
            <td>
              <a v-if="item.published_url" :href="item.published_url" target="_blank" rel="noopener">Revisar</a>
              <span v-else-if="item.candidate_url" class="text-success">Detectada</span>
              <span v-else>Pendiente</span>
            </td>
            <td class="text-end">
              <button type="button" class="btn btn-sm btn-primary" @click="openFollowUp(item)">
                <i class="fas fa-tasks" aria-hidden="true"></i><span class="ms-2">Gestionar</span>
              </button>
            </td>
          </tr>
          </template>
        </tbody>
      </table>
    </div>

    <nav v-if="pagination.total > 0" class="pagination-bar" aria-label="Páginas de seguimientos">
      <span>Mostrando {{ pagination.from }} a {{ pagination.to }} de {{ pagination.total }}</span>
      <div class="pagination-actions">
        <button type="button" class="btn btn-outline-secondary btn-sm" title="Página anterior"
          :disabled="loading || pagination.current_page === 1" @click="loadFollowUps(pagination.current_page - 1)">
          <i class="fas fa-chevron-left" aria-hidden="true"></i>
        </button>
        <button v-for="page in paginationPages" :key="page" type="button" class="btn btn-sm"
          :class="page === pagination.current_page ? 'btn-primary' : 'btn-outline-secondary'"
          :aria-current="page === pagination.current_page ? 'page' : null" @click="loadFollowUps(page)">
          {{ page }}
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" title="Página siguiente"
          :disabled="loading || pagination.current_page === pagination.last_page" @click="loadFollowUps(pagination.current_page + 1)">
          <i class="fas fa-chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </nav>

    <div v-if="modalOpen" class="modal-layer" role="dialog" aria-modal="true" aria-labelledby="follow-up-title" @click.self="closeModal">
      <div class="follow-up-modal">
        <header class="modal-header-row">
          <div>
            <h2 id="follow-up-title">Pedido P{{ activeFollowUp.pedido_id }}</h2>
            <p>{{ activeFollowUp.titulo }}</p>
          </div>
          <button type="button" class="icon-button" title="Cerrar" aria-label="Cerrar" @click="closeModal">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <div v-if="modalLoading" class="modal-loading">
          <span class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>Cargando detalle...
        </div>
        <div v-else class="modal-body-content">
          <div class="order-meta">
            <div><span>Estado</span><strong class="status-badge" :class="statusClass(activeFollowUp.status)">{{ activeFollowUp.status_label }}</strong></div>
            <div><span>Website</span><a :href="activeFollowUp.website.sitio" target="_blank" rel="noopener">{{ domain(activeFollowUp.website.sitio) }}</a></div>
            <div><span>Contacto</span><strong>{{ activeFollowUp.website.nombre_saludo || activeFollowUp.website.contacto || 'Sin nombre' }}</strong></div>
            <div><span>Correo</span><strong>{{ activeFollowUp.website.email || 'Sin correo' }}</strong></div>
          </div>

          <section class="workflow-section">
            <div class="section-heading">
              <div><h3>Archivos para publicación</h3><p>Un documento DOC o DOCX y de una a cinco imágenes.</p></div>
              <span v-if="currentVersion">Versión {{ currentVersion }}</span>
            </div>

            <div v-if="activeFollowUp.current_attachments.length" class="attachment-list mb-3">
              <a v-for="file in activeFollowUp.current_attachments" :key="file.id" :href="file.download_url" class="attachment-link">
                <i :class="file.kind === 'article' ? 'fas fa-file-word' : 'fas fa-image'" aria-hidden="true"></i>
                <span>{{ file.original_name }}</span>
                <small>{{ formatBytes(file.size) }}</small>
              </a>
            </div>

            <div v-if="!isCompleted" class="upload-grid">
              <label class="file-field">
                <span>Artículo</span>
                <input type="file" accept=".doc,.docx" @change="selectArticle">
                <small>{{ articleFile ? articleFile.name : `DOC o DOCX con P${activeFollowUp.pedido_id} en el nombre` }}</small>
              </label>
              <label class="file-field">
                <span>Imágenes</span>
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple @change="selectImages">
                <small>{{ imageFiles.length ? imageFiles.length + ' seleccionadas' : 'Seleccionar de 1 a 5' }}</small>
              </label>
              <button type="button" class="btn btn-primary" :disabled="actionLoading || !articleFile || !imageFiles.length" @click="uploadFiles">
                <i class="fas fa-upload" aria-hidden="true"></i><span class="ms-2">Guardar archivos</span>
              </button>
            </div>
          </section>

          <section class="workflow-section">
            <div class="section-heading">
              <div><h3>Comunicación con el medio</h3><p>Revisa el contenido antes de enviar.</p></div>
              <div class="section-actions">
                <button v-if="canSendOrder" type="button" class="btn btn-primary btn-sm" @click="prepareEmail('order')">
                  <i class="fas fa-envelope" aria-hidden="true"></i><span class="ms-2">{{ hasSentOrderEmail ? 'Preparar nueva versión' : 'Preparar correo' }}</span>
                </button>
                <button v-if="canSendReminder" type="button" class="btn btn-outline-primary btn-sm" @click="prepareEmail('reminder')">
                  <i class="fas fa-bell" aria-hidden="true"></i><span class="ms-2">Preparar recordatorio</span>
                </button>
                <button v-if="canSendCorrection" type="button" class="btn btn-warning btn-sm" @click="prepareEmail('correction')">
                  <i class="fas fa-edit" aria-hidden="true"></i><span class="ms-2">Solicitar corrección</span>
                </button>
              </div>
            </div>

            <div v-if="emailMode" class="email-editor">
              <div class="mb-2"><strong>Para:</strong> {{ emailPreview.recipient }}</div>
              <label class="form-label" for="email-subject">Asunto</label>
              <input id="email-subject" v-model="emailPreview.subject" type="text" class="form-control mb-3"
                maxlength="255" :readonly="emailMode !== 'order'">
              <label class="form-label" for="email-body-editor">Mensaje</label>
              <div ref="emailToolbar" class="email-format-toolbar" aria-label="Formato del mensaje">
                <span class="ql-formats">
                  <button type="button" class="ql-undo" title="Deshacer" aria-label="Deshacer"><i class="fas fa-undo" aria-hidden="true"></i></button>
                  <button type="button" class="ql-redo" title="Rehacer" aria-label="Rehacer"><i class="fas fa-redo" aria-hidden="true"></i></button>
                </span>
                <span class="ql-formats">
                  <select class="ql-font" title="Fuente" aria-label="Fuente">
                    <option selected></option>
                    <option value="serif"></option>
                    <option value="monospace"></option>
                  </select>
                  <select class="ql-size" title="Tamaño" aria-label="Tamaño de texto">
                    <option value="small"></option>
                    <option selected></option>
                    <option value="large"></option>
                    <option value="huge"></option>
                  </select>
                </span>
                <span class="ql-formats">
                  <button type="button" class="ql-bold" title="Negritas" aria-label="Negritas"></button>
                  <button type="button" class="ql-italic" title="Cursivas" aria-label="Cursivas"></button>
                  <button type="button" class="ql-underline" title="Subrayado" aria-label="Subrayado"></button>
                  <select class="ql-color" title="Color del texto" aria-label="Color del texto"></select>
                </span>
                <span class="ql-formats">
                  <select class="ql-align" title="Alineación" aria-label="Alineación"></select>
                  <button type="button" class="ql-list" value="ordered" title="Lista numerada" aria-label="Lista numerada"></button>
                  <button type="button" class="ql-list" value="bullet" title="Lista con viñetas" aria-label="Lista con viñetas"></button>
                  <button type="button" class="ql-clean" title="Quitar formato" aria-label="Quitar formato"></button>
                </span>
              </div>
              <div id="email-body-editor" ref="emailEditor" class="email-body-editor" aria-label="Mensaje"></div>
              <div class="email-actions">
                <button type="button" class="btn btn-light" @click="closeEmailEditor">Cancelar</button>
                <button type="button" class="btn btn-primary" :disabled="actionLoading" @click="sendEmail">
                  <i class="fas fa-paper-plane" aria-hidden="true"></i>
                  <span class="ms-2">{{ emailActionLabel }}</span>
                </button>
              </div>
            </div>
          </section>

          <section class="workflow-section">
            <div class="section-heading">
              <div><h3>URL de publicación</h3><p>La URL no modifica el pedido hasta confirmar la revisión.</p></div>
              <a v-if="activeFollowUp.google_url" :href="activeFollowUp.google_url" target="_blank" rel="noopener" class="btn btn-outline-secondary btn-sm">
                <i class="fab fa-google" aria-hidden="true"></i><span class="ms-2">Buscar en Google</span>
              </a>
            </div>

            <div class="article-title-reference">
              <span>Título registrado en el pedido</span>
              <strong>{{ activeFollowUp.titulo }}</strong>
            </div>

            <div v-if="activeFollowUp.title_checked_at" class="title-verification"
              :class="titleVerificationClass">
              <div>
                <strong>{{ titleVerificationLabel }}</strong>
                <span v-if="activeFollowUp.published_title">Título publicado: {{ activeFollowUp.published_title }}</span>
                <span v-else>{{ activeFollowUp.title_check_error }}</span>
              </div>
              <i :class="titleVerificationIcon" aria-hidden="true"></i>
            </div>

            <div v-if="activeFollowUp.candidate_url" class="candidate-url">
              <div><strong>URL detectada automáticamente</strong><a :href="activeFollowUp.candidate_url" target="_blank" rel="noopener">{{ activeFollowUp.candidate_url }}</a></div>
              <button type="button" class="btn btn-outline-success btn-sm" @click="urlInput = activeFollowUp.candidate_url">Usar URL</button>
            </div>

            <div class="url-row">
              <input v-model.trim="urlInput" type="url" class="form-control" placeholder="https://sitio.com/publicacion">
              <button type="button" class="btn btn-outline-secondary copy-url-button" title="Copiar URL"
                aria-label="Copiar URL" :disabled="!urlInput" @click="copyUrl">
                <i class="fas fa-copy" aria-hidden="true"></i>
              </button>
              <button v-if="canSaveUrl" type="button" class="btn btn-primary" :disabled="actionLoading || !urlInput" @click="saveUrl">Guardar para revisión</button>
              <button v-if="canComplete" type="button" class="btn btn-success" :disabled="actionLoading" @click="completeFollowUp">
                <i class="fas fa-check" aria-hidden="true"></i>
                <span class="ms-2">{{ activeFollowUp.status === 'correction_requested' ? 'Confirmar corrección' : 'Confirmar publicación' }}</span>
              </button>
            </div>
          </section>

          <section v-if="!isCompleted" class="workflow-section">
            <div class="section-heading"><div><h3>Estado manual</h3><p>Utiliza estos estados cuando el proceso requiera intervención.</p></div></div>
            <div class="status-row">
              <select v-model="manualStatus" class="form-select">
                <option value="">Seleccionar estado</option>
                <option v-for="option in manualStatusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
              </select>
              <button type="button" class="btn btn-outline-secondary" :disabled="actionLoading || !manualStatus" @click="updateStatus">Actualizar estado</button>
            </div>
          </section>

          <section class="workflow-section history-section">
            <div class="section-heading"><div><h3>Historial</h3><p>Archivos, correos, recordatorios y cambios del seguimiento.</p></div></div>
            <div v-if="!activeFollowUp.events.length" class="text-muted">Todavía no hay eventos.</div>
            <div v-for="event in activeFollowUp.events" :key="event.id" class="history-item">
              <div class="history-icon" :class="event.result === 'error' ? 'error' : ''"><i :class="eventIcon(event.type)" aria-hidden="true"></i></div>
              <div>
                <strong>{{ event.description }}</strong>
                <span>{{ event.recipient || event.user || 'Sistema' }} · {{ formatDateTime(event.created_at) }}</span>
                <p v-if="event.type === 'email.reply' && event.body" class="reply-preview">{{ event.body }}</p>
                <a v-if="event.meta && event.meta.matched_url" :href="event.meta.matched_url" target="_blank" rel="noopener" class="reply-url">
                  {{ event.meta.matched_url }}
                </a>
                <small v-if="event.error_message">{{ event.error_message }}</small>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Quill from 'quill';
import { markRaw } from 'vue';
import 'quill/dist/quill.snow.css';

export default {
  data() {
    return {
      followUps: [],
      pagination: { current_page: 1, last_page: 1, per_page: 20, total: 0, from: null, to: null },
      counts: {},
      statusOptions: [],
      selectedStatus: '',
      search: '',
      loading: false,
      modalLoading: false,
      actionLoading: false,
      modalOpen: false,
      activeFollowUp: null,
      articleFile: null,
      imageFiles: [],
      emailMode: '',
      emailPreview: { recipient: '', subject: '', body: '' },
      emailEditor: null,
      urlInput: '',
      manualStatus: '',
      message: '',
      error: '',
      gmailStatus: null,
      syncLoading: false,
      searchTimer: null,
      messageTimer: null,
      errorTimer: null,
    };
  },
  computed: {
    filteredFollowUps() {
      return this.followUps;
    },
    totalCount() {
      return Object.values(this.counts).reduce((total, value) => total + Number(value), 0);
    },
    isCompleted() {
      return this.activeFollowUp && this.activeFollowUp.status === 'completed';
    },
    currentVersion() {
      const files = this.activeFollowUp ? this.activeFollowUp.current_attachments : [];
      return files.length ? Math.max(...files.map(file => file.version)) : null;
    },
    canSendOrder() {
      return this.activeFollowUp && this.activeFollowUp.status === 'ready_to_send';
    },
    hasSentOrderEmail() {
      const events = this.activeFollowUp ? (this.activeFollowUp.events || []) : [];
      return events.some(event => event.type === 'email.order' && event.result === 'success');
    },
    canSendReminder() {
      return this.activeFollowUp && ['sent', 'no_response'].includes(this.activeFollowUp.status);
    },
    canSendCorrection() {
      return this.activeFollowUp
        && this.activeFollowUp.status === 'correction_requested'
        && Boolean(this.activeFollowUp.published_url);
    },
    canSaveUrl() {
      return this.activeFollowUp && ['sent', 'no_response', 'correction_requested', 'published_review'].includes(this.activeFollowUp.status);
    },
    canComplete() {
      return this.activeFollowUp
        && ['published_review', 'correction_requested'].includes(this.activeFollowUp.status)
        && Boolean(this.activeFollowUp.published_url);
    },
    manualStatusOptions() {
      return this.activeFollowUp ? (this.activeFollowUp.manual_status_options || []) : [];
    },
    titleVerificationLabel() {
      if (!this.activeFollowUp || this.activeFollowUp.title_matches === null) return 'Título no comprobado';
      return this.activeFollowUp.title_matches ? 'El título coincide' : 'El título no coincide';
    },
    titleVerificationClass() {
      if (!this.activeFollowUp || this.activeFollowUp.title_matches === null) return 'title-unverified';
      return this.activeFollowUp.title_matches ? 'title-match' : 'title-mismatch';
    },
    titleVerificationIcon() {
      if (!this.activeFollowUp || this.activeFollowUp.title_matches === null) return 'fas fa-exclamation-circle';
      return this.activeFollowUp.title_matches ? 'fas fa-check-circle' : 'fas fa-times-circle';
    },
    emailActionLabel() {
      if (this.emailMode === 'reminder') return 'Enviar recordatorio';
      if (this.emailMode === 'correction') return 'Enviar solicitud de corrección';
      return this.hasSentOrderEmail ? 'Enviar nueva versión' : 'Enviar correo';
    },
    paginationPages() {
      const last = this.pagination.last_page;
      const current = this.pagination.current_page;
      let start = Math.max(1, current - 2);
      const end = Math.min(last, start + 4);
      start = Math.max(1, end - 4);

      return Array.from({ length: end - start + 1 }, (_, index) => start + index);
    },
  },
  watch: {
    search() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.loadFollowUps(1), 350);
    },
    message(value) {
      window.clearTimeout(this.messageTimer);
      if (value) this.messageTimer = window.setTimeout(() => { this.message = ''; }, 5000);
    },
    error(value) {
      window.clearTimeout(this.errorTimer);
      if (value) this.errorTimer = window.setTimeout(() => { this.error = ''; }, 8000);
    },
  },
  methods: {
    async loadGmailStatus() {
      try {
        const response = await axios.get('/google/gmail/status');
        this.gmailStatus = response.data;
      } catch (_) {
        this.gmailStatus = null;
      }
    },
    async disconnectGmail() {
      if (!window.confirm('¿Deseas desconectar la cuenta de Gmail?')) return;
      try {
        await axios.delete('/google/gmail/disconnect');
        this.message = 'Cuenta de Gmail desconectada.';
        await this.loadGmailStatus();
      } catch (error) {
        this.showError(error);
      }
    },
    async syncReplies() {
      this.syncLoading = true;
      this.error = '';
      try {
        const response = await axios.post('/seguimiento-publicaciones/sincronizar-respuestas');
        this.message = response.data.message;
        await this.loadFollowUps();
        if (this.activeFollowUp) await this.refreshActiveFollowUp();
      } catch (error) {
        this.showError(error);
      } finally {
        this.syncLoading = false;
      }
    },
    async loadFollowUps(page = 1) {
      this.loading = true;
      this.error = '';
      try {
        const params = { page };
        if (this.selectedStatus) params.status = this.selectedStatus;
        if (this.search) params.q = this.search;
        const response = await axios.get('/seguimiento-publicaciones/listar', { params });
        this.followUps = response.data.follow_ups;
        this.pagination = response.data.pagination;
        this.counts = response.data.counts;
        this.statusOptions = response.data.status_options;
      } catch (error) {
        this.showError(error);
      } finally {
        this.loading = false;
      }
    },
    setStatusFilter(status) {
      this.selectedStatus = status;
      this.loadFollowUps(1);
    },
    async openFollowUp(item) {
      this.modalOpen = true;
      this.modalLoading = true;
      this.activeFollowUp = item;
      this.resetActionState();
      try {
        await this.refreshActiveFollowUp();
      } catch (error) {
        this.showError(error);
        this.modalOpen = false;
      } finally {
        this.modalLoading = false;
      }
    },
    async refreshActiveFollowUp() {
      const response = await axios.get(`/seguimiento-publicaciones/${this.activeFollowUp.id}`);
      this.activeFollowUp = response.data.follow_up;
      this.urlInput = this.activeFollowUp.published_url || this.activeFollowUp.candidate_url || '';
    },
    closeModal() {
      this.modalOpen = false;
      this.activeFollowUp = null;
      this.resetActionState();
    },
    resetActionState() {
      this.articleFile = null;
      this.imageFiles = [];
      this.emailMode = '';
      this.emailEditor = null;
      this.emailPreview = { recipient: '', subject: '', body: '' };
      this.urlInput = '';
      this.manualStatus = '';
    },
    selectArticle(event) {
      const file = event.target.files[0] || null;
      const validationMessage = file ? this.articleNameValidationMessage(file.name) : '';
      if (validationMessage) {
        this.articleFile = null;
        event.target.value = '';
        this.error = validationMessage;
        return;
      }

      this.articleFile = file;
      this.error = '';
    },
    articleNameValidationMessage(filename) {
      const orderNumber = String(this.activeFollowUp.pedido_id);
      if (new RegExp(`(^|[^0-9])P?${orderNumber}([^0-9]|$)`, 'i').test(filename)) return '';

      const mismatch = filename.match(/(?:^|[^a-z0-9])P([0-9]+)(?:[^0-9]|$)/i);
      if (mismatch) {
        return `El número de pedido del artículo (P${mismatch[1]}) no coincide con el pedido de la pantalla (P${orderNumber}).`;
      }

      return `El nombre del artículo debe incluir el número del pedido P${orderNumber}.`;
    },
    selectImages(event) {
      this.imageFiles = Array.from(event.target.files || []).slice(0, 5);
      if ((event.target.files || []).length > 5) this.error = 'Puedes seleccionar como máximo cinco imágenes.';
    },
    async uploadFiles() {
      const data = new FormData();
      data.append('article', this.articleFile);
      this.imageFiles.forEach(file => data.append('images[]', file));
      await this.runAction(async () => {
        await axios.post(`/seguimiento-publicaciones/${this.activeFollowUp.id}/archivos`, data);
      }, 'Archivos guardados correctamente.');
    },
    async prepareEmail(type) {
      this.actionLoading = true;
      this.error = '';
      try {
        const response = await axios.get(`/seguimiento-publicaciones/${this.activeFollowUp.id}/plantilla`, { params: { type } });
        this.emailPreview = response.data;
        this.emailMode = type;
        await this.$nextTick();
        this.initializeEmailEditor();
      } catch (error) {
        this.showError(error);
      } finally {
        this.actionLoading = false;
      }
    },
    async sendEmail() {
      const actions = { order: 'enviar', reminder: 'recordatorio', correction: 'correccion' };
      const isNewVersion = this.emailMode === 'order' && this.hasSentOrderEmail;
      const labels = {
        order: isNewVersion ? 'la nueva versión en el hilo original' : 'el correo con los archivos',
        reminder: 'el recordatorio',
        correction: 'la solicitud de corrección',
      };
      const action = actions[this.emailMode];
      const label = labels[this.emailMode];
      const body = this.emailEditor
        ? this.emailEditor.getText().replace(/\n$/, '')
        : this.emailPreview.body;
      const bodyHtml = this.emailEditor ? this.emailEditor.root.innerHTML : '';

      if (!body.trim()) {
        this.error = 'El mensaje no puede estar vacío.';
        return;
      }

      if (!window.confirm(`¿Deseas enviar ${label} a ${this.emailPreview.recipient}?`)) return;

      await this.runAction(async () => {
        await axios.post(`/seguimiento-publicaciones/${this.activeFollowUp.id}/${action}`, {
          subject: this.emailPreview.subject,
          body,
          body_html: bodyHtml,
        });
      }, this.emailMode === 'reminder'
        ? 'Recordatorio enviado correctamente.'
        : (this.emailMode === 'correction'
          ? 'Solicitud de corrección enviada correctamente.'
          : (isNewVersion ? 'Nueva versión enviada en el hilo original.' : 'Correo enviado correctamente.')));
    },
    initializeEmailEditor() {
      if (!this.$refs.emailEditor || !this.$refs.emailToolbar) return;

      const editor = markRaw(new Quill(this.$refs.emailEditor, {
        theme: 'snow',
        placeholder: 'Escribe el mensaje...',
        modules: {
          history: { delay: 500, maxStack: 100, userOnly: true },
          toolbar: {
            container: this.$refs.emailToolbar,
            handlers: {
              undo() { this.quill.history.undo(); },
              redo() { this.quill.history.redo(); },
            },
          },
        },
      }));
      editor.setText(this.emailPreview.body || '');
      editor.on('text-change', () => {
        if (editor.getLength() > 20001) {
          editor.deleteText(20000, editor.getLength());
        }
      });
      this.emailEditor = editor;
    },
    closeEmailEditor() {
      this.emailMode = '';
      this.emailEditor = null;
    },
    async saveUrl() {
      let responseMessage = 'URL guardada para revisión.';
      await this.runAction(async () => {
        const response = await axios.put(`/seguimiento-publicaciones/${this.activeFollowUp.id}/url`, { published_url: this.urlInput });
        responseMessage = response.data.message || responseMessage;
      }, () => responseMessage);
    },
    async copyUrl() {
      if (!this.urlInput) return;

      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.urlInput);
        } else {
          const field = document.createElement('textarea');
          field.value = this.urlInput;
          field.setAttribute('readonly', '');
          field.style.position = 'fixed';
          field.style.opacity = '0';
          document.body.appendChild(field);
          field.select();
          document.execCommand('copy');
          document.body.removeChild(field);
        }
        this.message = 'URL copiada al portapapeles.';
      } catch (_) {
        this.error = 'No fue posible copiar la URL.';
      }
    },
    async completeFollowUp() {
      const message = this.activeFollowUp.status === 'correction_requested'
        ? '¿Confirmas que la corrección ya fue realizada y revisada? Esta acción actualizará el pedido original.'
        : '¿Confirmas que revisaste la publicación? Esta acción actualizará el pedido original.';
      if (!window.confirm(message)) return;
      await this.runAction(async () => {
        await axios.post(`/seguimiento-publicaciones/${this.activeFollowUp.id}/concluir`);
      }, 'Publicación confirmada y seguimiento concluido.');
    },
    async updateStatus() {
      await this.runAction(async () => {
        await axios.put(`/seguimiento-publicaciones/${this.activeFollowUp.id}/estado`, { status: this.manualStatus });
      }, 'Estado actualizado correctamente.');
    },
    async runAction(action, successMessage) {
      this.actionLoading = true;
      this.error = '';
      try {
        await action();
        this.message = typeof successMessage === 'function' ? successMessage() : successMessage;
        this.emailMode = '';
        this.articleFile = null;
        this.imageFiles = [];
        this.manualStatus = '';
        await this.refreshActiveFollowUp();
        await this.loadFollowUps();
      } catch (error) {
        this.showError(error);
      } finally {
        this.actionLoading = false;
      }
    },
    showError(error) {
      const errors = error.response && error.response.data && error.response.data.errors;
      this.error = errors
        ? Object.values(errors).flat().join(' ')
        : (error.response && error.response.data && error.response.data.message) || 'No fue posible completar la operación.';
    },
    domain(url) {
      if (!url) return 'Sin Website';
      try { return new URL(url).hostname.replace(/^www\./i, ''); } catch (_) { return url; }
    },
    attachmentSummary(files) {
      if (!files || !files.length) return 'Pendientes';
      const images = files.filter(file => file.kind === 'image').length;
      return `1 artículo · ${images} ${images === 1 ? 'imagen' : 'imágenes'}`;
    },
    formatDate(value) {
      if (!value) return 'Pendiente';
      return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium' }).format(new Date(value));
    },
    formatDateTime(value) {
      if (!value) return '';
      return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
    },
    formatBytes(value) {
      if (!value) return '0 KB';
      return `${(Number(value) / 1024 / 1024).toFixed(2)} MB`;
    },
    statusClass(status) {
      return `status-${status}`;
    },
    eventIcon(type) {
      if (type.includes('email')) return 'fas fa-envelope';
      if (type.includes('attachment')) return 'fas fa-paperclip';
      if (type.includes('publication')) return 'fas fa-link';
      return 'fas fa-history';
    },
  },
  mounted() {
    const params = new URLSearchParams(window.location.search);
    const gmailResult = params.get('gmail');
    if (gmailResult === 'connected') this.message = `Gmail conectado correctamente${params.get('account') ? ': ' + params.get('account') : ''}.`;
    if (gmailResult === 'cancelled') this.error = 'La autorización de Gmail fue cancelada.';
    if (gmailResult === 'state-error') this.error = 'La autorización de Gmail expiró. Intenta conectarla nuevamente.';
    if (gmailResult === 'error') this.error = 'No fue posible completar la conexión con Gmail.';
    if (gmailResult) window.history.replaceState({}, document.title, window.location.pathname);

    this.loadGmailStatus();
    this.loadFollowUps();
  },
  beforeUnmount() {
    window.clearTimeout(this.searchTimer);
    window.clearTimeout(this.messageTimer);
    window.clearTimeout(this.errorTimer);
  },
};
</script>

<style scoped>
.gmail-status { display: flex; align-items: center; justify-content: space-between; gap: 16px; border: 1px solid #d9dee5; background: #fff; padding: 12px 14px; }
.gmail-status > div:first-child { display: flex; align-items: center; gap: 12px; min-width: 0; }
.gmail-status .fa-google { color: #1a73e8; font-size: 20px; }
.gmail-status strong, .gmail-status span { display: block; }
.gmail-status span { color: #5f6b7a; font-size: 13px; margin-top: 2px; }
.gmail-actions { display: flex; gap: 8px; flex-shrink: 0; }
.summary-bar { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 4px; }
.summary-item { min-width: 132px; height: 56px; display: flex; align-items: center; justify-content: space-between; gap: 12px; border: 1px solid #d8dee5; border-radius: 6px; background: #fff; color: #303840; padding: 0 12px; text-align: left; }
.summary-item span { font-size: 13px; line-height: 1.15; }
.summary-item strong { font-size: 18px; }
.summary-item.active { border-color: #0d6efd; box-shadow: inset 0 0 0 1px #0d6efd; color: #0b5ed7; }
.toolbar-row { display: flex; justify-content: space-between; gap: 12px; }
.toolbar-actions { display: flex; gap: 8px; flex-shrink: 0; }
.search-box { position: relative; flex: 1; max-width: 520px; }
.search-box i { position: absolute; left: 12px; top: 12px; color: #66717c; }
.search-box input { padding-left: 36px; }
.follow-up-table-wrap { border: 1px solid #d8dee5; background: #fff; }
.pagination-bar { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 0; color: #5f6b76; font-size: 13px; }
.pagination-actions { display: flex; gap: 5px; }
.pagination-actions .btn { min-width: 34px; }
.follow-up-table-wrap th { white-space: nowrap; background: #f4f6f8; font-size: 13px; color: #48535e; }
.follow-up-table-wrap td { font-size: 14px; min-width: 110px; }
.follow-up-table-wrap th:last-child, .follow-up-table-wrap td:last-child { position: sticky; right: 0; z-index: 1; min-width: 132px; background: #fff; box-shadow: -5px 0 8px rgba(25, 35, 45, .06); }
.follow-up-table-wrap th:last-child { z-index: 2; background: #f4f6f8; }
.follow-up-table-wrap tr:hover td:last-child { background: #f7f9fb; }
.follow-up-table-wrap td small, .order-cell span { display: block; margin-top: 3px; color: #69737d; font-size: 12px; }
.order-cell { max-width: 300px; }
.order-cell span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.empty-row { height: 180px; text-align: center; color: #69737d; }
.status-badge { display: inline-flex; align-items: center; min-height: 26px; border-radius: 14px; padding: 4px 10px; font-size: 12px; font-weight: 600; white-space: nowrap; background: #e9ecef; color: #3d4650; }
.status-pending_article { background: #eef1f4; color: #4f5b66; }
.status-ready_to_send { background: #e3f0ff; color: #0759b5; }
.status-sent { background: #e6f6ee; color: #127143; }
.status-correction_requested { background: #fff3cd; color: #7a5900; }
.status-published_review { background: #e8f5e9; color: #246b2c; }
.status-no_response { background: #fff0dc; color: #8a4b00; }
.status-rejected, .status-cancelled { background: #f8e4e6; color: #9a2430; }
.status-completed { background: #dff3e5; color: #146c43; }
.modal-layer { position: fixed; inset: 0; z-index: 1060; display: flex; justify-content: flex-end; background: rgba(17, 24, 31, .42); }
.follow-up-modal { width: min(900px, 100%); height: 100%; background: #fff; overflow-y: auto; box-shadow: -8px 0 24px rgba(0,0,0,.16); }
.modal-header-row { position: sticky; top: 0; z-index: 2; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 20px 24px; border-bottom: 1px solid #dfe4e8; background: #fff; }
.modal-header-row h2 { margin: 0; font-size: 22px; }
.modal-header-row p { margin: 4px 0 0; color: #68737d; }
.icon-button { width: 38px; height: 38px; border: 0; background: transparent; color: #49545f; }
.icon-button:hover { background: #edf0f2; }
.modal-loading { padding: 80px 24px; text-align: center; color: #68737d; }
.modal-body-content { padding: 0 24px 32px; }
.order-meta { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; padding: 18px 0; border-bottom: 1px solid #e3e7ea; }
.order-meta div { min-width: 0; }
.order-meta span { display: block; margin-bottom: 5px; color: #68737d; font-size: 12px; }
.order-meta strong, .order-meta a { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.workflow-section { padding: 22px 0; border-bottom: 1px solid #e3e7ea; }
.section-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; margin-bottom: 14px; }
.section-heading h3 { margin: 0; font-size: 17px; }
.section-heading p { margin: 4px 0 0; color: #68737d; font-size: 13px; }
.section-actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.attachment-list { display: flex; flex-wrap: wrap; gap: 8px; }
.attachment-link { display: grid; grid-template-columns: 20px minmax(80px, 1fr); column-gap: 7px; align-items: center; max-width: 260px; border: 1px solid #d8dee5; border-radius: 5px; padding: 8px 10px; color: #1f5f9f; text-decoration: none; }
.attachment-link span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attachment-link small { grid-column: 2; color: #68737d; }
.upload-grid { display: grid; grid-template-columns: 1fr 1fr auto; gap: 10px; align-items: end; }
.file-field { min-width: 0; border: 1px solid #ced4da; border-radius: 5px; padding: 8px 10px; }
.file-field span { display: block; font-size: 12px; font-weight: 600; }
.file-field input { width: 100%; margin: 4px 0; font-size: 12px; }
.file-field small { display: block; overflow: hidden; color: #68737d; text-overflow: ellipsis; white-space: nowrap; }
.email-editor { border-left: 3px solid #0d6efd; padding: 4px 0 4px 16px; }
.email-format-toolbar.ql-toolbar { display: flex; flex-wrap: wrap; gap: 2px; border: 1px solid #ced4da; border-bottom: 0; border-radius: 5px 5px 0 0; background: #f6f8fb; }
.email-format-toolbar.ql-toolbar .ql-formats { display: inline-flex; align-items: center; margin-right: 6px; }
.email-format-toolbar.ql-toolbar button { width: 30px; height: 30px; }
.email-format-toolbar.ql-toolbar .ql-undo i,
.email-format-toolbar.ql-toolbar .ql-redo i { display: block; color: #444; font-size: 14px; line-height: 18px; }
.email-format-toolbar.ql-toolbar .ql-undo:hover i,
.email-format-toolbar.ql-toolbar .ql-redo:hover i { color: #06c; }
.email-body-editor.ql-container { min-height: 250px; max-height: 440px; overflow-y: auto; border-color: #ced4da; border-radius: 0 0 5px 5px; font-family: Arial, sans-serif; font-size: 15px; }
.email-body-editor .ql-editor { min-height: 250px; line-height: 1.55; }
.email-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
.article-title-reference { margin-bottom: 12px; border-left: 3px solid #0d6efd; padding: 8px 12px; background: #f5f8fc; }
.article-title-reference span, .article-title-reference strong { display: block; }
.article-title-reference span { margin-bottom: 3px; color: #68737d; font-size: 12px; }
.article-title-reference strong { overflow-wrap: anywhere; }
.title-verification { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; border: 1px solid; padding: 10px 12px; }
.title-verification strong, .title-verification span { display: block; }
.title-verification span { margin-top: 3px; font-size: 13px; overflow-wrap: anywhere; }
.title-verification i { flex-shrink: 0; font-size: 18px; }
.title-match { border-color: #9bc9aa; background: #eef8f1; color: #175f36; }
.title-mismatch { border-color: #e2b1b6; background: #fff1f2; color: #8a2430; }
.title-unverified { border-color: #d8c486; background: #fff8df; color: #735800; }
.candidate-url { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; padding: 10px 12px; background: #eef8f1; border: 1px solid #badbc4; }
.candidate-url strong, .candidate-url a { display: block; }
.candidate-url a { max-width: 600px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.url-row, .status-row { display: grid; grid-template-columns: 1fr auto auto auto; gap: 8px; }
.copy-url-button { width: 40px; min-width: 40px; padding-left: 0; padding-right: 0; }
.status-row { grid-template-columns: minmax(220px, 360px) auto; }
.history-section { border-bottom: 0; }
.history-item { display: grid; grid-template-columns: 34px 1fr; gap: 10px; padding: 9px 0; }
.history-icon { width: 30px; height: 30px; display: grid; place-items: center; border-radius: 50%; background: #e9f2ff; color: #1769be; }
.history-icon.error { background: #f9e4e6; color: #a32632; }
.history-item span, .history-item small { display: block; margin-top: 2px; color: #68737d; font-size: 12px; }
.history-item small { color: #a32632; }
.reply-preview { max-width: 680px; margin: 6px 0 0; color: #49545f; font-size: 13px; white-space: pre-line; }
.reply-url { display: block; max-width: 680px; margin-top: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 767px) {
  .gmail-status, .toolbar-row, .section-heading, .candidate-url, .pagination-bar { align-items: stretch; flex-direction: column; }
  .gmail-actions, .toolbar-actions { flex-wrap: wrap; }
  .order-meta { grid-template-columns: 1fr 1fr; }
  .upload-grid, .url-row, .status-row { grid-template-columns: 1fr; }
  .modal-body-content, .modal-header-row { padding-left: 16px; padding-right: 16px; }
}
</style>
